import { useEffect, useRef, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion, type PanInfo } from "motion/react";
import { IconButton } from "./ui";
import { haptic } from "../telegram/haptics";
import { sheetSpring, micro } from "../motion";
import { useT } from "../i18n/strings";
import s from "./Sheet.module.css";

interface SheetProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  footer?: ReactNode;
  headerAction?: ReactNode;
}

/** Past this much travel or this much flick, the gesture reads as "dismiss". */
const DISMISS_DISTANCE = 110;
const DISMISS_VELOCITY = 550;

export function Sheet({ open, onClose, title, children, footer, headerAction }: SheetProps) {
  const t = useT();

  /*
   * A sheet arriving and a sheet leaving are felt here rather than at the
   * button that opened it. Every caller used to fire its own `press` before
   * flipping the flag, which left the paths that close a sheet without one —
   * backdrop tap, Escape, the header X — silent, and meant a new caller had to
   * remember. One effect on the flag covers every way in and every way out.
   */
  const wasOpen = useRef(open);
  useEffect(() => {
    // Only a change is an event. Pickers mount their sheet closed and stay
    // mounted, so firing on the first run would buzz once per field on a form.
    if (wasOpen.current === open) return;
    wasOpen.current = open;
    if (open) haptic.press();
    else haptic.tap();
  }, [open]);

  // A sheet over a scrollable list must not let the list scroll underneath it.
  useEffect(() => {
    if (!open) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => event.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const onDragEnd = (_event: unknown, info: PanInfo) => {
    if (info.offset.y > DISMISS_DISTANCE || info.velocity.y > DISMISS_VELOCITY) {
      onClose();
    }
  };

  return createPortal(
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            className={s.backdrop}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={micro}
            onClick={onClose}
          />
          <motion.div
            className={`${s.sheet} chrome`}
            role="dialog"
            aria-modal="true"
            aria-label={title}
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={sheetSpring}
            drag="y"
            dragDirectionLock
            dragConstraints={{ top: 0, bottom: 0 }}
            dragElastic={{ top: 0, bottom: 0.6 }}
            onDragEnd={onDragEnd}
          >
            <div className={s.grip} aria-hidden>
              <span className={s.gripBar} />
            </div>
            <header className={s.header}>
              <h2 className={s.title}>{title}</h2>
              <div className={s.headerActions}>
                {headerAction}
                <IconButton name="close" label={t.actions.close} onClick={onClose} />
              </div>
            </header>
            <div className={s.body}>{children}</div>
            {footer && <div className={s.footer}>{footer}</div>}
          </motion.div>
        </>
      )}
    </AnimatePresence>,
    document.body,
  );
}
